import React, { PureComponent } from "react";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Table from "react-bootstrap/Table";
import {
  ChecklistFieldDef,
  ChecklistRepeatableGroupDef,
  ChecklistSectionDef,
  renderChecklistFieldTypeBadges,
} from "./DynamicChecklistManagement";
import {
  DuplicateTargetOption,
  reorderAdjacentFields,
} from "./duplicateFieldKeys";
import {
  MasterFieldDuplicateRowContext,
  MasterFieldDuplicateTable,
} from "./MasterFieldDuplicateTable";

interface Props {
  section: ChecklistSectionDef;
  group: ChecklistRepeatableGroupDef;
  editingFieldId: string | null;
  enumerateTargets: (sourceKeys: string[]) => DuplicateTargetOption[];
  onDuplicate: (sourceKeys: string[], targetKey: string) => void;
  onReorder: (sectionId: string, groupId: string, fields: ChecklistFieldDef[]) => void;
  onEdit: (sectionId: string, groupId: string, field: ChecklistFieldDef) => void;
  onRemove: (sectionId: string, groupId: string, fieldId: string) => void;
  resolveDynamicFieldLabel?: (field: ChecklistFieldDef) => string | null;
}

/**
 * Isolated deed / product / survey group field table so reorder and row
 * actions do not re-render the full DynamicChecklistManagement page.
 */
export class DeedGroupFieldTable extends PureComponent<Props> {
  private handleMove = (fieldId: string, direction: -1 | 1) => {
    const { section, group, onReorder } = this.props;
    const next = reorderAdjacentFields(group.fields, fieldId, direction);
    if (next) onReorder(section.id, group.id, next);
  };

  private renderRow = (ctx: MasterFieldDuplicateRowContext) => {
    const { section, group, editingFieldId, onEdit, onRemove, resolveDynamicFieldLabel } =
      this.props;
    const dynamicFieldLabel = resolveDynamicFieldLabel?.(ctx.field) ?? null;
    return (
      <tr className={editingFieldId === ctx.field.id ? "table-active" : undefined}>
        <td className="dvc-col-select">{ctx.selectionCell}</td>
        <td className="dvc-col-num">{ctx.index + 1}</td>
        <td className="dvc-field-label-cell">
          {ctx.field.label}
          {ctx.field.required && <span className="dvc-req-pill">Required</span>}
          {ctx.field.disabled && <span className="dvc-dis-pill">Disabled</span>}
        </td>
        <td>{renderChecklistFieldTypeBadges(ctx.field)}</td>
        <td>
          <span className="dvc-layout-muted">{ctx.field.layout ?? "half"}</span>
        </td>
        <td>
          {ctx.field.required ? (
            <span className="dvc-req-yes">Yes</span>
          ) : (
            <span className="dvc-req-dash" aria-hidden>
              —
            </span>
          )}
        </td>
        <td>
          {ctx.field.disabled ? (
            <span className="dvc-dis-yes">Yes</span>
          ) : (
            <span className="dvc-req-dash" aria-hidden>
              —
            </span>
          )}
        </td>
        <td className="dvc-dynamic-field-cell">
          {dynamicFieldLabel ? (
            <span
              className="small d-inline-block text-truncate"
              style={{ maxWidth: 240 }}
              title={dynamicFieldLabel}
            >
              {dynamicFieldLabel}
            </span>
          ) : (
            <span className="dvc-req-dash" aria-hidden>
              —
            </span>
          )}
        </td>
        <td className="text-right text-nowrap">
          <ButtonGroup size="sm" className="dvc-section-actions">
            <Button
              variant="outline-secondary"
              title="Move field up"
              onClick={() => this.handleMove(ctx.field.id, -1)}
            >
              ↑
            </Button>
            <Button
              variant="outline-secondary"
              title="Move field down"
              onClick={() => this.handleMove(ctx.field.id, 1)}
            >
              ↓
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => onEdit(section.id, group.id, ctx.field)}
            >
              Edit
            </Button>
            <Button
              variant="outline-info"
              title="Duplicate this field to another section or document type"
              onClick={ctx.openSingleDuplicatePicker}
            >
              Duplicate
            </Button>
            <Button
              variant="outline-danger"
              onClick={() => onRemove(section.id, group.id, ctx.field.id)}
            >
              Remove
            </Button>
          </ButtonGroup>
        </td>
      </tr>
    );
  };

  render() {
    const { section, group, enumerateTargets, onDuplicate } = this.props;
    return (
      <Table responsive size="sm" className="mb-2 bg-light dvc-field-table">
        <MasterFieldDuplicateTable
          sectionId={section.id}
          groupId={group.id}
          fields={group.fields}
          colSpan={9}
          enumerateTargets={enumerateTargets}
          onDuplicate={onDuplicate}
          renderChrome={(api) => (
            <>
              <thead className="thead-light">
                <tr>
                  <th className="dvc-col-select border-0" title="Select for bulk duplicate">
                    {api.renderSelectAllHeader()}
                  </th>
                  <th className="dvc-col-num border-0">#</th>
                  <th className="border-0">Label</th>
                  <th style={{ width: 108 }} className="border-0">
                    Type
                  </th>
                  <th style={{ width: 88 }} className="border-0">
                    Layout
                  </th>
                  <th style={{ width: 96 }} className="border-0">
                    Required
                  </th>
                  <th style={{ width: 96 }} className="border-0">
                    Disable
                  </th>
                  <th className="border-0">Dynamic field value</th>
                  <th style={{ width: 200 }} className="text-right border-0">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>{api.renderBody()}</tbody>
            </>
          )}
          renderRow={this.renderRow}
        />
      </Table>
    );
  }
}
